<template>
    <div>
        <PaginationTable
            :columns="columns"
            :data="reportingCoursesTableData"
            :columnHiddenOption="columnHiddenOption"
            :event-custom-option="eventCustomOption"
            class="courses-table"
            v-on:pageNumberChange="pageNumberChange"
            v-on:pageSizeChange="pageSizeChange"
            v-on:sortChange="sortChange"
            :totalCount="rowCount"
            :page="page"
            :dataType="$t('OrganizationReport.courses_pagination_amount')"
        />
        <ModalCourseTable />
    </div>
</template>

<script>
    import PaginationTable from 'components/user/organizationreport/tables/PaginationTable'
    import convertMinutesToHours from 'components/shared/mixins/convertMinutesToHours'
    import ModalCourseTable from 'components/user/organizationreport/tabs/courses/modal/ModalCourseTable'
    import { mapActions, mapGetters } from 'vuex'
    import { translateWithDefault } from 'helpers/i18nHelper'

    export default {
        name: 'courses-table',
        components: { PaginationTable, ModalCourseTable },
        mixins: [convertMinutesToHours],
        props: {
            reportingCoursesTableData: {
                type: Array,
                default: null
            }
        },
        data () {
            return {
                columnHiddenOption: {
                    defaultHiddenColumnKeys: ['id', 'email', 'status'],
                },
                eventCustomOption: {
                    bodyRowEvents: ({ row, rowIndex }) => {
                        return {
                            click: (event) => {
                                this.$store.dispatch('ReportsCourses/loadCourseDetails', row.id)
                                this.$modal.show('reporting-course-table')
                            }
                        }
                    }
                },
                columns: [
                    { field: 'id', key: 'id', title: 'Id' },
                    { field: 'title',
                      key: 'title',
                      title: this.$t('ReportingTable.name'),
                      align: 'left',
                      sortBy: '',
                      width: '19%',
                      renderBodyCell: ({ row, column, rowIndex }, h) => {
                          const courseName = row[column.field]
                          const instructor = row.instructor
                          const level = row.level

                          let courseNameClasses = 'table-cell --course-name'
                          let instructorClasses = 'table-cell --instructor'
                          let levelClasses = 'table-cell --level'

                          if (row.status === 'expired') {
                              courseNameClasses = 'table-cell --course-name --expired'
                              instructorClasses = 'table-cell --instructor --expired'
                              levelClasses = 'table-cell --level --expired'
                          }

                          return (

                            <div>
                                <span class={ courseNameClasses }>{ courseName }</span>
                                <span class={ instructorClasses }>{ this.$t('ReportingTable.instructor') }: { instructor }</span>
                                <span class={ levelClasses }>{ this.$t('ReportingTable.level') }: { this.$t(`Select.skill_level_${level}`) }</span>
                            </div>
                        )
                      } },
                    { field: 'type', key: 'type', title: this.$t('ReportingTable.learning_format'), align: 'left', sortBy: '', width: '13%' },
                    { field: 'duration',
                      key: 'duration',
                      title: this.$t('ReportingTable.duration'),
                      align: 'left',
                      sortBy: '',
                      width: '8%',
                      renderBodyCell: ({ row, column, rowIndex }, h) => {
                          const duration = row[column.field]
                          let time = this.convertMinutesToHours(duration)
                          return time
                      }
                    },
                    { field: 'subjectAreas',
                      key: 'subjectAreas',
                      title: this.$t('ReportingTable.subject_areas'),
                      align: 'left',
                      width: '12%',
                      renderBodyCell: ({ row, column, rowIndex }, h) => {
                          const subjectAreaArray = row[column.field]
                          const subjectAreas = subjectAreaArray.map(subject => {
                              return translateWithDefault(subject, subject)
                          })
                          return subjectAreas.join(', ')
                      }

                    },
                    { field: 'categories',
                      key: 'categories',
                      title: this.$t('ReportingTable.categories'),
                      align: 'left',
                      width: '12%',
                      renderBodyCell: ({ row, column, rowIndex }, h) => {
                          const categories = row[column.field]
                          return categories.join(', ')
                      }
                    },
                    { field: 'completed', key: 'completed', title: this.$t('ReportingTable.completed'), align: 'left', sortBy: '', width: '10%' },
                    { field: 'uncompleted', key: 'uncompleted', title: this.$t('ReportingTable.uncompleted'), align: 'left', sortBy: '', width: '11%' },
                    { field: 'totalMinutesWatched',
                      key: 'totalMinutesWatched',
                      title: this.$t('ReportingTable.total_watched'),
                      align: 'left',
                      sortBy: 'desc',
                      width: '15%',
                      renderBodyCell: ({ row, column, rowIndex }, h) => {
                          const totalWatched = row[column.field]
                          let time = this.convertMinutesToHours(totalWatched)
                          return this.addExpiredClass(row, time)
                      }
                    }
                ]
            }
        },
        computed: {
            ...mapGetters({
                rowCount: 'ReportsCourses/rowCount',
                page: 'ReportsCourses/getPage'
            })
        },
        methods: {
            pageNumberChange (pageIndex) {
                this.$store.dispatch('ReportsCourses/pageNumberChange', pageIndex)
            },
            pageSizeChange (pageSize) {
                this.$store.dispatch('ReportsCourses/pageSizeChange', pageSize)
            },
            sortChange (sortParams) {
                const {
                    title: courseTitle,
                    type: courseType,
                    duration: courseDuration,
                    completed: totalCompleted,
                    uncompleted: totalUncompleted,
                    totalMinutesWatched: totalWatched,
                    ...rest
                } = sortParams

                const renamedSortParams = {
                    courseTitle,
                    courseType,
                    courseDuration,
                    totalCompleted,
                    totalUncompleted,
                    totalWatched,
                    ...rest
                }
                this.$store.dispatch('ReportsCourses/sortOrderChange', renamedSortParams)
            },
            addExpiredClass (row, columnField) {
                if (row.status === 'expired') {
                    return <span class="table-cell --expired">{columnField}</span>
                } else {
                    return <span>{columnField}</span>
                }
            }
        }
    }
</script>

<style lang="scss">
    .courses-table {
        .table-cell {
            &.--course-name {
                font-family: Nunito-ExtraBold, sans-serif;
                display: block;
            }

            &.--instructor {
                color: $emperor;
                display: block;
                margin: 5px 0 0;
            }

            &.--level {
                color: $emperor;
                display: block;
                margin: 5px 0 0;
            }

            &.--expired {
                color: $gray;
            }
        }
    }
</style>
